import { resolve, dirname, basename } from "path";
import { existsSync, realpathSync } from "fs";
import { open as openFile, stat as statFile } from "fs/promises";
import { TTLCache } from "@isaacs/ttlcache";
import type { ParsedDiffFile } from "../server/utils/diff-highlighter.js";
import type { GitHubSearchKind, GitLabSearchKind } from "../shared/messages.js";
import { parseAndHighlightDiff } from "../server/utils/diff-highlighter.js";
import {
  GitHubAuthenticationError,
  GitHubCliMissingError,
  createGitHubService,
  resolveGitHubRepo,
  type GitHubService,
} from "../services/github-service.js";
import {
  GitLabAuthenticationError,
  GitLabCliMissingError,
  createGitLabService,
  type GitLabService,
} from "../services/gitlab-service.js";
import { execCommand } from "./spawn.js";
import { findExecutable } from "./executable.js";
import { parseGitRevParsePath, resolveGitRevParsePath } from "./git-rev-parse-path.js";
import { runGitCommand } from "./run-git-command.js";
import { isAppostleOwnedWorktreeCwd } from "./worktree.js";
import { requireAppostleWorktreeBaseRefName } from "./worktree-metadata.js";
const READ_ONLY_GIT_ENV: NodeJS.ProcessEnv = {
  ...process.env,
  GIT_OPTIONAL_LOCKS: "0",
};

const DEFAULT_PULL_REQUEST_STATUS_CACHE_TTL_MS = 30_000;
const PULL_REQUEST_STATUS_CACHE_MAX = 1_000;

let pullRequestStatusCacheTtlMs = DEFAULT_PULL_REQUEST_STATUS_CACHE_TTL_MS;
let pullRequestStatusCache = createPullRequestStatusCache(pullRequestStatusCacheTtlMs);
const pullRequestStatusInFlight = new Map<string, Promise<PullRequestStatusResult>>();

function createPullRequestStatusCache(ttlMs: number) {
  return new TTLCache<string, PullRequestStatusResult>({
    ttl: ttlMs,
    max: PULL_REQUEST_STATUS_CACHE_MAX,
    checkAgeOnGet: true,
  });
}

function getPullRequestStatusCacheKey(cwd: string): string {
  return resolve(cwd);
}

export function __resetPullRequestStatusCacheForTests(): void {
  pullRequestStatusCache.clear();
  pullRequestStatusCache.cancelTimer();
  pullRequestStatusCacheTtlMs = DEFAULT_PULL_REQUEST_STATUS_CACHE_TTL_MS;
  pullRequestStatusCache = createPullRequestStatusCache(pullRequestStatusCacheTtlMs);
  pullRequestStatusInFlight.clear();
}

export function __setPullRequestStatusCacheTtlForTests(ttlMs: number): void {
  pullRequestStatusCache.clear();
  pullRequestStatusCache.cancelTimer();
  pullRequestStatusCacheTtlMs = ttlMs;
  pullRequestStatusCache = createPullRequestStatusCache(ttlMs);
  pullRequestStatusInFlight.clear();
}

type CheckoutFileChange = {
  path: string;
  oldPath?: string;
  status: string;
  isNew: boolean;
  isDeleted: boolean;
  isUntracked?: boolean;
};

function normalizeBranchSuggestionName(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  let normalized = trimmed;
  if (normalized.startsWith("refs/heads/")) {
    normalized = normalized.slice("refs/heads/".length);
  } else if (normalized.startsWith("refs/remotes/")) {
    normalized = normalized.slice("refs/remotes/".length);
  }

  if (normalized.startsWith("origin/")) {
    normalized = normalized.slice("origin/".length);
  }

  if (!normalized || normalized === "HEAD" || normalized === "origin") {
    return null;
  }

  return normalized;
}

interface GitRef {
  name: string;
  committerDate: number;
}

export interface BranchSuggestion {
  name: string;
  committerDate: number;
  hasLocal: boolean;
  hasRemote: boolean;
  /**
   * True when the local branch is fully merged into the workspace's base
   * branch (e.g. main). Computed in bulk via `git for-each-ref --merged`.
   * Undefined when no base ref is supplied (callers that don't care, or
   * when the workspace doesn't have a resolvable base).
   */
  isMergedIntoBase?: boolean;
}

async function listGitRefs(cwd: string, refPrefix: string): Promise<GitRef[]> {
  const { stdout } = await runGitCommand(
    [
      "for-each-ref",
      "--sort=-committerdate",
      "--format=%(refname)%09%(committerdate:unix)",
      refPrefix,
    ],
    { cwd, env: READ_ONLY_GIT_ENV },
  );
  return stdout
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      const [name, dateStr] = trimmed.split("\t");
      if (!name) return null;
      return { name, committerDate: Number(dateStr) || 0 };
    })
    .filter((ref): ref is GitRef => ref !== null);
}

interface BranchSuggestionMeta {
  committerDate: number;
  hasLocal: boolean;
  hasRemote: boolean;
}

function sortBranchSuggestions(
  branchNames: string[],
  branchMeta: Map<string, BranchSuggestionMeta>,
  query: string,
): string[] {
  const normalizedQuery = query.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;
  return branchNames.sort((a, b) => {
    if (hasQuery) {
      const aPrefix = a.toLowerCase().startsWith(normalizedQuery);
      const bPrefix = b.toLowerCase().startsWith(normalizedQuery);
      if (aPrefix !== bPrefix) {
        return aPrefix ? -1 : 1;
      }
    }

    const aMeta = branchMeta.get(a);
    const bMeta = branchMeta.get(b);
    const aDate = aMeta?.committerDate ?? 0;
    const bDate = bMeta?.committerDate ?? 0;
    if (aDate !== bDate) {
      return bDate - aDate;
    }

    return a.localeCompare(b);
  });
}

/**
 * Returns the set of LOCAL branch names that are fully merged into
 * `baseRef`. Used to gate the "delete branch" affordance — a branch is
 * safe to delete when its tip is reachable from the base. Single git
 * subprocess; resolves to an empty Set on any error so the UI degrades
 * gracefully (delete just stays hidden).
 */
async function listLocalBranchesMergedInto(cwd: string, baseRef: string): Promise<Set<string>> {
  try {
    const { stdout } = await runGitCommand(
      ["for-each-ref", "--format=%(refname)", "--merged", baseRef, "refs/heads"],
      { cwd, env: READ_ONLY_GIT_ENV },
    );
    const merged = new Set<string>();
    for (const line of stdout.split("\n")) {
      const name = normalizeBranchSuggestionName(line.trim());
      if (name) merged.add(name);
    }
    return merged;
  } catch {
    return new Set<string>();
  }
}

export async function listBranchSuggestions(
  cwd: string,
  options?: { query?: string; limit?: number; baseRef?: string | null },
): Promise<BranchSuggestion[]> {
  await requireGitRepo(cwd);

  const requestedLimit = options?.limit ?? 50;
  const limit = Math.max(1, Math.min(200, requestedLimit));
  const query = options?.query?.trim().toLowerCase() ?? "";
  const rawBaseRef = options?.baseRef?.trim();
  const baseRef = rawBaseRef && rawBaseRef.length > 0 ? rawBaseRef.replace(/^origin\//, "") : null;

  const [localRefs, remoteRefs, mergedSet] = await Promise.all([
    listGitRefs(cwd, "refs/heads"),
    listGitRefs(cwd, "refs/remotes/origin"),
    baseRef ? listLocalBranchesMergedInto(cwd, baseRef) : Promise.resolve(new Set<string>()),
  ]);

  const branchMeta = new Map<string, BranchSuggestionMeta>();

  for (const ref of localRefs) {
    const normalized = normalizeBranchSuggestionName(ref.name);
    if (!normalized) continue;
    const existing = branchMeta.get(normalized);
    branchMeta.set(normalized, {
      hasLocal: true,
      hasRemote: existing?.hasRemote ?? false,
      committerDate: Math.max(ref.committerDate, existing?.committerDate ?? 0),
    });
  }

  for (const ref of remoteRefs) {
    const normalized = normalizeBranchSuggestionName(ref.name);
    if (!normalized) continue;
    const existing = branchMeta.get(normalized);
    if (!existing) {
      branchMeta.set(normalized, {
        hasLocal: false,
        hasRemote: true,
        committerDate: ref.committerDate,
      });
    } else {
      branchMeta.set(normalized, {
        ...existing,
        hasRemote: true,
        committerDate: Math.max(ref.committerDate, existing.committerDate),
      });
    }
  }

  const filteredNames = Array.from(branchMeta.keys()).filter((name) =>
    query ? name.toLowerCase().includes(query) : true,
  );
  if (filteredNames.length === 0) {
    return [];
  }

  const ordered = sortBranchSuggestions(filteredNames, branchMeta, query);
  return ordered.slice(0, limit).map((name) => {
    const meta = branchMeta.get(name);
    const hasLocal = meta?.hasLocal ?? false;
    return {
      name,
      committerDate: meta?.committerDate ?? 0,
      hasLocal,
      hasRemote: meta?.hasRemote ?? false,
      isMergedIntoBase: baseRef && hasLocal ? mergedSet.has(name) : undefined,
    };
  });
}

/**
 * Delete a local branch and (optionally) its origin counterpart in one
 * call.
 *
 * **Why we use `-D` (force) instead of `-d`**: `git branch -d` is the
 * "safe" variant — but it checks whether the branch is merged into its
 * **upstream tracking branch** (`origin/<branch>`), not into the actual
 * base branch (e.g. `main`). For our flow ("delete a branch I just
 * merged into main"), that's the wrong shape: a branch that's fully
 * merged into `main` but slightly ahead of its own remote (or has no
 * remote at all) gets refused with a confusing message.
 *
 * Instead, the CALLER is expected to verify "merged into base" using
 * `for-each-ref --merged <baseRef>` (already done by `listBranchSuggestions`
 * to gate the trash icon), and this function then performs an unconditional
 * force-delete locally. The policy gate in the UI is the primary safety
 * check; defense in depth lives in the caller, not here.
 *
 * Remote delete is best-effort: if it fails (offline, ref missing, no
 * permission) the local delete still stands and the error is reported
 * back so the UI can surface it.
 *
 * Refuses to delete the currently checked-out branch — git would block
 * this anyway, but we short-circuit with a clearer message.
 */
export interface DeleteBranchInput {
  branchName: string;
  deleteRemote: boolean;
}

export interface DeleteBranchResult {
  branchName: string;
  localDeleted: boolean;
  remoteDeleted: boolean;
  remoteError: string | null;
}

export async function deleteBranch(
  cwd: string,
  input: DeleteBranchInput,
): Promise<DeleteBranchResult> {
  await requireGitRepo(cwd);
  const branchName = input.branchName.trim();
  if (!branchName) {
    throw new Error("Branch name is required");
  }

  // Refuse self-delete with a clearer message than git's default.
  try {
    const { stdout } = await runGitCommand(["rev-parse", "--abbrev-ref", "HEAD"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const currentBranch = stdout.trim();
    if (currentBranch === branchName) {
      throw new Error(`Cannot delete the currently checked-out branch "${branchName}".`);
    }
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.startsWith("Cannot delete the currently checked-out branch")
    ) {
      throw error;
    }
    // Couldn't read HEAD — let the delete itself fail with git's message.
  }

  // Force-delete locally — `-D` because `-d` checks against the upstream
  // tracking branch, which is the wrong reference for our "merged into
  // base" semantic. See doc comment above.
  await runGitCommand(["branch", "-D", branchName], { cwd, timeout: 10_000 });

  let remoteDeleted = false;
  let remoteError: string | null = null;
  if (input.deleteRemote) {
    try {
      await runGitCommand(["push", "origin", "--delete", branchName], { cwd, timeout: 30_000 });
      remoteDeleted = true;
    } catch (error) {
      remoteError = error instanceof Error ? error.message : String(error);
    }
  }

  return {
    branchName,
    localDeleted: true,
    remoteDeleted,
    remoteError,
  };
}

export interface LocalBranchCheckoutResolution {
  kind: "local";
  name: string;
}

export interface RemoteOnlyBranchCheckoutResolution {
  kind: "remote-only";
  name: string;
  remoteRef: string;
}

export interface NotFoundBranchCheckoutResolution {
  kind: "not-found";
}

export type BranchCheckoutResolution =
  | LocalBranchCheckoutResolution
  | RemoteOnlyBranchCheckoutResolution
  | NotFoundBranchCheckoutResolution;

export async function resolveBranchCheckout(
  cwd: string,
  name: string,
): Promise<BranchCheckoutResolution> {
  await requireGitRepo(cwd);

  const normalized = normalizeBranchSuggestionName(name);
  if (!normalized) {
    return { kind: "not-found" };
  }

  const localRef = `refs/heads/${normalized}`;
  const localResult = await runGitCommand(["rev-parse", "--verify", "--quiet", localRef], {
    cwd,
    env: READ_ONLY_GIT_ENV,
    acceptExitCodes: [0, 1],
  });
  const hasLocal = localResult.exitCode === 0;
  if (hasLocal) {
    return { kind: "local", name: normalized };
  }

  const remoteRef = `origin/${normalized}`;
  const remoteRefPath = `refs/remotes/${remoteRef}`;
  const remoteResult = await runGitCommand(["rev-parse", "--verify", "--quiet", remoteRefPath], {
    cwd,
    env: READ_ONLY_GIT_ENV,
    acceptExitCodes: [0, 1],
  });
  const hasRemote = remoteResult.exitCode === 0;
  if (hasRemote) {
    return { kind: "remote-only", name: normalized, remoteRef };
  }

  return { kind: "not-found" };
}

export type BranchCheckoutSource = "local" | "remote";

export interface CheckoutExistingBranchResult {
  source: BranchCheckoutSource;
}

export interface CheckoutResolvedBranchInput {
  cwd: string;
  resolution: BranchCheckoutResolution;
  requestedBranch?: string;
}

export async function checkoutResolvedBranch(
  input: CheckoutResolvedBranchInput,
): Promise<CheckoutExistingBranchResult> {
  const { cwd, resolution } = input;

  switch (resolution.kind) {
    case "local": {
      const { stdout } = await runGitCommand(["rev-parse", "--abbrev-ref", "HEAD"], { cwd });
      const current = stdout.trim();
      if (current === resolution.name) {
        return { source: "local" };
      }

      await runGitCommand(["checkout", resolution.name], { cwd });
      return { source: "local" };
    }
    case "remote-only":
      await runGitCommand(["checkout", "-b", resolution.name, "--track", resolution.remoteRef], {
        cwd,
      });
      return { source: "remote" };
    case "not-found":
      throw new Error(`Branch not found: ${input.requestedBranch ?? "unknown"}`);
  }
}

async function listCheckoutFileChanges(
  cwd: string,
  ref: string,
  ignoreWhitespace = false,
): Promise<CheckoutFileChange[]> {
  const changes: CheckoutFileChange[] = [];

  const { stdout: nameStatusOut } = await runGitCommand(
    buildGitDiffArgs({
      ignoreWhitespace,
      extra: ["--name-status", ref],
    }),
    { cwd, env: READ_ONLY_GIT_ENV },
  );
  for (const line of nameStatusOut
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)) {
    // `--name-status` uses TAB separators, which preserves filenames with spaces.
    const tabParts = line.split("\t");
    const rawStatus = (tabParts[0] ?? "").trim();
    if (!rawStatus) continue;

    if (rawStatus.startsWith("R") || rawStatus.startsWith("C")) {
      const oldPath = tabParts[1];
      const newPath = tabParts[2];
      if (newPath) {
        changes.push({
          path: newPath,
          ...(oldPath ? { oldPath } : {}),
          status: rawStatus,
          isNew: false,
          isDeleted: false,
        });
      }
      continue;
    }

    const path = tabParts[1];
    if (!path) continue;
    const code = rawStatus[0];
    changes.push({
      path,
      status: rawStatus,
      isNew: code === "A",
      isDeleted: code === "D",
    });
  }

  const { stdout: untrackedOut } = await runGitCommand(
    ["ls-files", "--others", "--exclude-standard"],
    {
      cwd,
      env: READ_ONLY_GIT_ENV,
    },
  );
  for (const file of untrackedOut
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)) {
    changes.push({
      path: file,
      status: "U",
      isNew: true,
      isDeleted: false,
      isUntracked: true,
    });
  }

  // Deduplicate by path (prefer tracked status over untracked marker if both appear).
  const byPath = new Map<string, CheckoutFileChange>();
  for (const change of changes) {
    const existing = byPath.get(change.path);
    if (!existing) {
      byPath.set(change.path, change);
      continue;
    }
    if (existing.isUntracked && !change.isUntracked) {
      byPath.set(change.path, change);
    }
  }
  return Array.from(byPath.values());
}

async function readGitFileContentAtRef(
  cwd: string,
  ref: string,
  path: string,
): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["show", `${ref}:${path}`], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    return stdout;
  } catch {
    return null;
  }
}

async function tryResolveMergeBase(cwd: string, baseRef: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["merge-base", baseRef, "HEAD"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const sha = stdout.trim();
    return sha.length > 0 ? sha : null;
  } catch {
    return null;
  }
}

type FileStat = { additions: number; deletions: number; isBinary: boolean } | null;

function normalizeNumstatPath(pathField: string): string {
  const braceRenameMatch = pathField.match(/^(.*)\{(.*) => (.*)\}(.*)$/);
  if (braceRenameMatch) {
    const [, prefix, , renamed, suffix] = braceRenameMatch;
    return `${prefix}${renamed}${suffix}`;
  }

  const inlineRenameMatch = pathField.match(/^(.*) => (.*)$/);
  if (inlineRenameMatch) {
    return inlineRenameMatch[2] ?? pathField;
  }

  return pathField;
}

function buildGitDiffArgs(args: { ignoreWhitespace?: boolean; extra: string[] }): string[] {
  return ["diff", ...(args.ignoreWhitespace ? ["-w"] : []), ...args.extra];
}

const TRACKED_DIFF_NUMSTAT_MAX_BYTES = 2 * 1024 * 1024; // 2MB
const TRACKED_MAX_CHANGED_LINES = 40_000;

async function getTrackedNumstatByPath(
  cwd: string,
  ref: string,
  ignoreWhitespace = false,
): Promise<Map<string, FileStat>> {
  const result = await runGitCommand(
    buildGitDiffArgs({
      ignoreWhitespace,
      extra: ["--numstat", ref],
    }),
    {
      cwd,
      env: READ_ONLY_GIT_ENV,
      maxOutputBytes: TRACKED_DIFF_NUMSTAT_MAX_BYTES,
      acceptExitCodes: [0],
    },
  );

  const stats = new Map<string, FileStat>();
  const lines = result.stdout
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    const parts = line.split("\t");
    if (parts.length < 3) {
      continue;
    }

    const additionsField = parts[0] ?? "";
    const deletionsField = parts[1] ?? "";
    const rawPath = parts.slice(2).join("\t");
    const path = normalizeNumstatPath(rawPath);

    if (!path) {
      continue;
    }

    if (additionsField === "-" || deletionsField === "-") {
      stats.set(path, { additions: 0, deletions: 0, isBinary: true });
      continue;
    }

    const additions = Number.parseInt(additionsField, 10);
    const deletions = Number.parseInt(deletionsField, 10);
    if (Number.isNaN(additions) || Number.isNaN(deletions)) {
      stats.set(path, null);
      continue;
    }

    stats.set(path, { additions, deletions, isBinary: false });
  }

  return stats;
}

function isTrackedDiffTooLarge(stat: FileStat): boolean {
  if (!stat || stat.isBinary) {
    return false;
  }
  return stat.additions + stat.deletions > TRACKED_MAX_CHANGED_LINES;
}

export class NotGitRepoError extends Error {
  readonly cwd: string;
  readonly code = "NOT_GIT_REPO";

  constructor(cwd: string) {
    super(`Not a git repository: ${cwd}`);
    this.name = "NotGitRepoError";
    this.cwd = cwd;
  }
}

export class MergeConflictError extends Error {
  readonly baseRef: string;
  readonly currentBranch: string;
  readonly conflictFiles: string[];

  constructor(options: { baseRef: string; currentBranch: string; conflictFiles: string[] }) {
    super(`Merge conflict while merging ${options.currentBranch} into ${options.baseRef}`);
    this.name = "MergeConflictError";
    this.baseRef = options.baseRef;
    this.currentBranch = options.currentBranch;
    this.conflictFiles = options.conflictFiles;
  }
}

export class MergeFromBaseConflictError extends Error {
  readonly baseRef: string;
  readonly currentBranch: string;
  readonly conflictFiles: string[];

  constructor(options: { baseRef: string; currentBranch: string; conflictFiles: string[] }) {
    super(
      `Merge conflict while merging ${options.baseRef} into ${options.currentBranch}. Please merge manually.`,
    );
    this.name = "MergeFromBaseConflictError";
    this.baseRef = options.baseRef;
    this.currentBranch = options.currentBranch;
    this.conflictFiles = options.conflictFiles;
  }
}

export interface AheadBehind {
  ahead: number;
  behind: number;
}

export interface CheckoutStatus {
  isGit: false;
}

export type CheckoutStatusGitNonAppostle = {
  isGit: true;
  repoRoot: string;
  currentBranch: string | null;
  isDirty: boolean;
  baseRef: string | null;
  aheadBehind: AheadBehind | null;
  aheadOfOrigin: number | null;
  behindOfOrigin: number | null;
  hasRemote: boolean;
  remoteUrl: string | null;
  isAppostleOwnedWorktree: false;
};

export type CheckoutStatusGitAppostle = {
  isGit: true;
  repoRoot: string;
  mainRepoRoot: string;
  currentBranch: string | null;
  isDirty: boolean;
  baseRef: string;
  aheadBehind: AheadBehind | null;
  aheadOfOrigin: number | null;
  behindOfOrigin: number | null;
  hasRemote: boolean;
  remoteUrl: string | null;
  isAppostleOwnedWorktree: true;
};

export type CheckoutStatusGit = CheckoutStatusGitNonAppostle | CheckoutStatusGitAppostle;

export type CheckoutStatusResult = CheckoutStatus | CheckoutStatusGit;

export interface CheckoutDiffResult {
  diff: string;
  structured?: ParsedDiffFile[];
}

export interface CheckoutDiffCompare {
  mode: "uncommitted" | "base";
  baseRef?: string;
  ignoreWhitespace?: boolean;
  includeStructured?: boolean;
}

export interface MergeToBaseOptions {
  baseRef?: string;
  mode?: "merge" | "squash";
  commitMessage?: string;
}

export interface MergeFromBaseOptions {
  baseRef?: string;
  requireCleanTarget?: boolean;
}

export type CheckoutContext = {
  appostleHome?: string;
};

function isGitError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return /not a git repository/i.test(error.message) || /git repository/i.test(error.message);
}

async function requireGitRepo(cwd: string): Promise<void> {
  try {
    await runGitCommand(["rev-parse", "--git-dir"], { cwd, env: READ_ONLY_GIT_ENV });
  } catch (error) {
    throw new NotGitRepoError(cwd);
  }
}

export async function getCurrentBranch(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["rev-parse", "--abbrev-ref", "HEAD"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const branch = stdout.trim();
    return branch.length > 0 ? branch : null;
  } catch {
    return null;
  }
}

async function getWorktreeRoot(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["rev-parse", "--show-toplevel"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    return parseGitRevParsePath(stdout);
  } catch {
    return null;
  }
}

export async function getMainRepoRoot(cwd: string): Promise<string> {
  const { stdout: commonDirOut } = await runGitCommand(["rev-parse", "--git-common-dir"], {
    cwd,
    env: READ_ONLY_GIT_ENV,
  });
  const commonDir = resolveGitRevParsePath(cwd, commonDirOut);
  if (!commonDir) {
    throw new Error("Not in a git repository");
  }
  const normalized = realpathSync(commonDir);

  if (basename(normalized) === ".git") {
    return dirname(normalized);
  }

  const { stdout: worktreeOut } = await runGitCommand(["worktree", "list", "--porcelain"], {
    cwd,
    env: READ_ONLY_GIT_ENV,
  });
  const worktrees = parseWorktreeList(worktreeOut);
  const nonBareNonAppostle = worktrees.filter(
    (wt) => !wt.isBare && !isAppostleWorktreePath(wt.path),
  );
  const childrenOfBareRepo = nonBareNonAppostle.filter((wt) =>
    isDescendantPath(wt.path, normalized),
  );
  const mainChild = childrenOfBareRepo.find((wt) => basename(wt.path) === "main");
  return (
    mainChild?.path ?? childrenOfBareRepo[0]?.path ?? nonBareNonAppostle[0]?.path ?? normalized
  );
}

export type GitWorktreeEntry = {
  path: string;
  branchRef?: string;
  isBare?: boolean;
};

/** Check whether a path contains a `.appostle/worktrees/` segment (both `/` and `\`). */
export function isAppostleWorktreePath(p: string): boolean {
  return /[/\\]\.appostle[/\\]worktrees[/\\]/.test(p);
}

/** True when `child` is strictly inside `parent` (handles both `/` and `\`). */
export function isDescendantPath(child: string, parent: string): boolean {
  let c = child.replace(/\\/g, "/").replace(/\/+$/, "");
  let p = parent.replace(/\\/g, "/").replace(/\/+$/, "");
  // Case-insensitive on Windows (drive letter like C: or D:)
  if (/^[A-Za-z]:/.test(c) || /^[A-Za-z]:/.test(p)) {
    c = c.toLowerCase();
    p = p.toLowerCase();
  }
  if (!c.startsWith(p)) return false;
  if (c.length === p.length) return false;
  return c[p.length] === "/";
}

export function parseWorktreeList(output: string): GitWorktreeEntry[] {
  const entries: GitWorktreeEntry[] = [];
  let current: GitWorktreeEntry | null = null;
  for (const line of output.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) {
      continue;
    }
    if (trimmed.startsWith("worktree ")) {
      if (current) {
        entries.push(current);
      }
      current = { path: trimmed.slice("worktree ".length).trim() };
      continue;
    }
    if (current && trimmed.startsWith("branch ")) {
      current.branchRef = trimmed.slice("branch ".length).trim();
    }
    if (current && trimmed === "bare") {
      current.isBare = true;
    }
  }
  if (current) {
    entries.push(current);
  }
  return entries;
}

async function getWorktreePathForBranch(cwd: string, branchName: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["worktree", "list", "--porcelain"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const entries = parseWorktreeList(stdout);
    const ref = branchName.startsWith("refs/heads/") ? branchName : `refs/heads/${branchName}`;
    return entries.find((entry) => entry.branchRef === ref)?.path ?? null;
  } catch {
    return null;
  }
}

export async function renameCurrentBranch(
  cwd: string,
  newName: string,
): Promise<{ previousBranch: string | null; currentBranch: string | null }> {
  await requireGitRepo(cwd);

  const previousBranch = await getCurrentBranch(cwd);
  if (!previousBranch || previousBranch === "HEAD") {
    throw new Error("Cannot rename branch in detached HEAD state");
  }

  await runGitCommand(["branch", "-m", newName], {
    cwd,
    timeout: 120_000,
  });

  const currentBranch = await getCurrentBranch(cwd);
  return { previousBranch, currentBranch };
}

type ConfiguredBaseRefForCwd =
  | { baseRef: null; isAppostleOwnedWorktree: false }
  | { baseRef: string; isAppostleOwnedWorktree: true };

async function getConfiguredBaseRefForCwd(
  cwd: string,
  context?: CheckoutContext,
): Promise<ConfiguredBaseRefForCwd> {
  // Fast-path reject: non-worktree paths do not need expensive ownership checks.
  if (!/[\\/]worktrees[\\/]/.test(cwd)) {
    return { baseRef: null, isAppostleOwnedWorktree: false };
  }

  const ownership = await isAppostleOwnedWorktreeCwd(cwd, { appostleHome: context?.appostleHome });
  if (!ownership.allowed) {
    return { baseRef: null, isAppostleOwnedWorktree: false };
  }

  const worktreeRoot = (await getWorktreeRoot(cwd)) ?? cwd;
  return {
    baseRef: requireAppostleWorktreeBaseRefName(worktreeRoot),
    isAppostleOwnedWorktree: true,
  };
}

async function isWorkingTreeDirty(cwd: string): Promise<boolean> {
  const { stdout } = await runGitCommand(["status", "--porcelain"], {
    cwd,
    env: READ_ONLY_GIT_ENV,
  });
  return stdout.trim().length > 0;
}

export async function getOriginRemoteUrl(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["config", "--get", "remote.origin.url"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const url = stdout.trim();
    return url.length > 0 ? url : null;
  } catch {
    return null;
  }
}

export async function hasOriginRemote(cwd: string): Promise<boolean> {
  const url = await getOriginRemoteUrl(cwd);
  return url !== null;
}

export async function resolveAbsoluteGitDir(cwd: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(["rev-parse", "--absolute-git-dir"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const gitDir = stdout.trim();
    return gitDir.length > 0 ? gitDir : null;
  } catch {
    return null;
  }
}

async function abortGitPullConflictState(cwd: string): Promise<void> {
  const gitDir = await resolveAbsoluteGitDir(cwd);
  if (!gitDir) {
    return;
  }

  const mergeHeadPath = resolve(gitDir, "MERGE_HEAD");
  const rebaseMergePath = resolve(gitDir, "rebase-merge");
  const rebaseApplyPath = resolve(gitDir, "rebase-apply");

  if (existsSync(mergeHeadPath)) {
    try {
      await runGitCommand(["merge", "--abort"], { cwd, timeout: 120_000 });
    } catch {
      // ignore
    }
  }

  if (existsSync(rebaseMergePath) || existsSync(rebaseApplyPath)) {
    try {
      await runGitCommand(["rebase", "--abort"], { cwd, timeout: 120_000 });
    } catch {
      // ignore
    }
  }
}

export async function resolveRepositoryDefaultBranch(repoRoot: string): Promise<string | null> {
  try {
    const { stdout } = await runGitCommand(
      ["symbolic-ref", "--quiet", "refs/remotes/origin/HEAD"],
      {
        cwd: repoRoot,
        env: READ_ONLY_GIT_ENV,
      },
    );
    const ref = stdout.trim();
    if (ref) {
      // Prefer a local branch name (e.g. "main") over the remote-tracking ref (e.g. "origin/main")
      // so that status/diff/merge all operate against the same base ref.
      const remoteShort = ref.replace(/^refs\/remotes\//, "");
      const localName = remoteShort.startsWith("origin/")
        ? remoteShort.slice("origin/".length)
        : remoteShort;
      try {
        await runGitCommand(["show-ref", "--verify", "--quiet", `refs/heads/${localName}`], {
          cwd: repoRoot,
          env: READ_ONLY_GIT_ENV,
        });
        return localName;
      } catch {
        return remoteShort;
      }
    }
  } catch {
    // ignore
  }

  const { stdout } = await runGitCommand(["branch", "--format=%(refname:short)"], {
    cwd: repoRoot,
    env: READ_ONLY_GIT_ENV,
  });
  const branches = stdout
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (branches.includes("main")) {
    return "main";
  }
  if (branches.includes("master")) {
    return "master";
  }

  return null;
}

async function resolveBaseRef(repoRoot: string): Promise<string | null> {
  return resolveRepositoryDefaultBranch(repoRoot);
}

function normalizeLocalBranchRefName(input: string): string {
  if (input.startsWith("refs/remotes/origin/")) {
    return input.slice("refs/remotes/origin/".length);
  }
  if (input.startsWith("refs/heads/")) {
    return input.slice("refs/heads/".length);
  }
  if (input.startsWith("origin/")) {
    return input.slice("origin/".length);
  }
  return input;
}

interface ComparisonBaseRefName {
  localName: string;
  originRef: string;
}

function normalizeComparisonBaseRefName(input: string): ComparisonBaseRefName {
  const localName = normalizeLocalBranchRefName(input);
  return { localName, originRef: `origin/${localName}` };
}

async function doesGitRefExist(cwd: string, fullRef: string): Promise<boolean> {
  const result = await runGitCommand(["show-ref", "--verify", "--quiet", fullRef], {
    cwd,
    env: READ_ONLY_GIT_ENV,
    acceptExitCodes: [0, 1],
  });
  return result.exitCode === 0;
}

async function resolveBestComparisonBaseRef(cwd: string, baseRef: string): Promise<string> {
  const normalized = normalizeComparisonBaseRefName(baseRef);
  const [hasLocal, hasOrigin] = await Promise.all([
    doesGitRefExist(cwd, `refs/heads/${normalized.localName}`),
    doesGitRefExist(cwd, `refs/remotes/origin/${normalized.localName}`),
  ]);

  if (hasOrigin) {
    return normalized.originRef;
  }
  if (hasLocal) {
    return normalized.localName;
  }

  const refName =
    baseRef.startsWith("origin/") || baseRef.startsWith("refs/remotes/origin/")
      ? normalized.originRef
      : normalized.localName;
  throw new Error(`Base branch not found locally or on origin: ${refName}`);
}

async function resolveMostAheadBaseRef(cwd: string, normalizedBaseRef: string): Promise<string> {
  const [hasLocal, hasOrigin] = await Promise.all([
    doesGitRefExist(cwd, `refs/heads/${normalizedBaseRef}`),
    doesGitRefExist(cwd, `refs/remotes/origin/${normalizedBaseRef}`),
  ]);

  if (hasLocal && !hasOrigin) {
    return normalizedBaseRef;
  }
  if (!hasLocal && hasOrigin) {
    return `origin/${normalizedBaseRef}`;
  }
  if (!hasLocal && !hasOrigin) {
    throw new Error(`Base branch not found locally or on origin: ${normalizedBaseRef}`);
  }

  const { stdout } = await runGitCommand(
    ["rev-list", "--left-right", "--count", `${normalizedBaseRef}...origin/${normalizedBaseRef}`],
    { cwd, env: READ_ONLY_GIT_ENV },
  );
  const [localOnlyRaw, originOnlyRaw] = stdout.trim().split(/\s+/);
  const localOnly = Number.parseInt(localOnlyRaw ?? "0", 10);
  const originOnly = Number.parseInt(originOnlyRaw ?? "0", 10);
  if (Number.isNaN(localOnly) || Number.isNaN(originOnly)) {
    return normalizedBaseRef;
  }
  if (originOnly > localOnly) {
    return `origin/${normalizedBaseRef}`;
  }

  return normalizedBaseRef;
}

async function getAheadBehind(
  cwd: string,
  baseRef: string,
  currentBranch: string,
): Promise<AheadBehind | null> {
  const normalizedBaseRef = normalizeLocalBranchRefName(baseRef);
  if (!normalizedBaseRef || !currentBranch || normalizedBaseRef === currentBranch) {
    return null;
  }
  const comparisonBaseRef = await resolveBestComparisonBaseRef(cwd, baseRef);
  const { stdout } = await runGitCommand(
    ["rev-list", "--left-right", "--count", `${comparisonBaseRef}...${currentBranch}`],
    { cwd, env: READ_ONLY_GIT_ENV },
  );
  const [behindRaw, aheadRaw] = stdout.trim().split(/\s+/);
  const behind = Number.parseInt(behindRaw ?? "0", 10);
  const ahead = Number.parseInt(aheadRaw ?? "0", 10);
  if (Number.isNaN(behind) || Number.isNaN(ahead)) {
    return null;
  }
  return { ahead, behind };
}

async function getAheadOfOrigin(cwd: string, currentBranch: string): Promise<number | null> {
  if (!currentBranch) {
    return null;
  }
  try {
    const { stdout } = await runGitCommand(
      ["rev-list", "--count", `origin/${currentBranch}..${currentBranch}`],
      { cwd, env: READ_ONLY_GIT_ENV },
    );
    const count = Number.parseInt(stdout.trim(), 10);
    return Number.isNaN(count) ? null : count;
  } catch {
    try {
      const { stdout } = await runGitCommand(["rev-list", "--count", currentBranch], {
        cwd,
        env: READ_ONLY_GIT_ENV,
      });
      const count = Number.parseInt(stdout.trim(), 10);
      return Number.isNaN(count) ? null : count;
    } catch {
      return null;
    }
  }
}

async function getBehindOfOrigin(cwd: string, currentBranch: string): Promise<number | null> {
  if (!currentBranch) {
    return null;
  }
  try {
    const { stdout } = await runGitCommand(
      ["rev-list", "--count", `${currentBranch}..origin/${currentBranch}`],
      { cwd, env: READ_ONLY_GIT_ENV },
    );
    const count = Number.parseInt(stdout.trim(), 10);
    return Number.isNaN(count) ? null : count;
  } catch {
    return null;
  }
}

type CheckoutInspectionContext = {
  worktreeRoot: string;
  currentBranch: string | null;
  remoteUrl: string | null;
  configured: ConfiguredBaseRefForCwd;
};

async function inspectCheckoutContext(
  cwd: string,
  context?: CheckoutContext,
): Promise<CheckoutInspectionContext | null> {
  try {
    const root = await getWorktreeRoot(cwd);
    if (!root) {
      return null;
    }

    const [currentBranch, remoteUrl, configured] = await Promise.all([
      getCurrentBranch(cwd),
      getOriginRemoteUrl(cwd),
      getConfiguredBaseRefForCwd(cwd, context),
    ]);

    return {
      worktreeRoot: root,
      currentBranch,
      remoteUrl,
      configured,
    };
  } catch (error) {
    if (isGitError(error)) {
      return null;
    }
    throw error;
  }
}

const PER_FILE_DIFF_MAX_BYTES = 1024 * 1024; // 1MB
const TOTAL_DIFF_MAX_BYTES = 2 * 1024 * 1024; // 2MB
const UNTRACKED_BINARY_SNIFF_BYTES = 16 * 1024;

async function isLikelyBinaryFile(absolutePath: string): Promise<boolean> {
  const handle = await openFile(absolutePath, "r");
  try {
    const buffer = Buffer.allocUnsafe(UNTRACKED_BINARY_SNIFF_BYTES);
    const { bytesRead } = await handle.read(buffer, 0, buffer.length, 0);
    if (bytesRead === 0) {
      return false;
    }

    let suspicious = 0;
    for (let i = 0; i < bytesRead; i += 1) {
      const byte = buffer[i];
      if (byte === 0) {
        return true;
      }
      // Treat control bytes as suspicious while allowing common whitespace.
      if (byte < 7 || (byte > 14 && byte < 32) || byte === 127) {
        suspicious += 1;
      }
    }

    return suspicious / bytesRead > 0.3;
  } finally {
    await handle.close();
  }
}

async function inspectUntrackedFile(
  cwd: string,
  relativePath: string,
): Promise<{ stat: FileStat; truncated: boolean }> {
  const absolutePath = resolve(cwd, relativePath);
  const metadata = await statFile(absolutePath);

  if (!metadata.isFile()) {
    return { stat: null, truncated: false };
  }

  if (await isLikelyBinaryFile(absolutePath)) {
    return {
      stat: { additions: 0, deletions: 0, isBinary: true },
      truncated: false,
    };
  }

  if (metadata.size > PER_FILE_DIFF_MAX_BYTES) {
    return {
      stat: { additions: 0, deletions: 0, isBinary: false },
      truncated: true,
    };
  }

  return {
    stat: { additions: 0, deletions: 0, isBinary: false },
    truncated: false,
  };
}

function buildPlaceholderParsedDiffFile(
  change: CheckoutFileChange,
  options: { status: "too_large" | "binary"; stat?: FileStat },
): ParsedDiffFile {
  return {
    path: change.path,
    isNew: change.isNew,
    isDeleted: change.isDeleted,
    additions: options.stat?.additions ?? 0,
    deletions: options.stat?.deletions ?? 0,
    hunks: [],
    status: options.status,
  };
}

async function getUntrackedDiffText(
  cwd: string,
  change: CheckoutFileChange,
  ignoreWhitespace = false,
): Promise<{ text: string; truncated: boolean; stat: FileStat }> {
  try {
    const inspected = await inspectUntrackedFile(cwd, change.path);
    if (inspected.stat?.isBinary || inspected.truncated) {
      return { text: "", truncated: inspected.truncated, stat: inspected.stat };
    }
  } catch {
    // Fall through to git diff path if metadata probing fails.
  }

  const result = await runGitCommand(
    buildGitDiffArgs({
      ignoreWhitespace,
      extra: ["--no-index", "/dev/null", "--", change.path],
    }),
    {
      cwd,
      env: READ_ONLY_GIT_ENV,
      maxOutputBytes: PER_FILE_DIFF_MAX_BYTES,
      acceptExitCodes: [0, 1],
    },
  );
  return {
    text: result.stdout,
    truncated: result.truncated,
    stat: { additions: 0, deletions: 0, isBinary: false },
  };
}

export async function getCheckoutStatus(
  cwd: string,
  context?: CheckoutContext,
): Promise<CheckoutStatusResult> {
  const inspected = await inspectCheckoutContext(cwd, context);
  if (!inspected) {
    return { isGit: false };
  }

  const worktreeRoot = inspected.worktreeRoot;
  const currentBranch = inspected.currentBranch;
  const remoteUrl = inspected.remoteUrl;
  const configured = inspected.configured;
  const isDirty = await isWorkingTreeDirty(cwd);
  const hasRemote = remoteUrl !== null;
  const baseRef = configured.baseRef ?? (await resolveBaseRef(cwd));
  const [aheadBehind, aheadOfOrigin, behindOfOrigin] = await Promise.all([
    baseRef && currentBranch ? getAheadBehind(cwd, baseRef, currentBranch) : Promise.resolve(null),
    hasRemote && currentBranch ? getAheadOfOrigin(cwd, currentBranch) : Promise.resolve(null),
    hasRemote && currentBranch ? getBehindOfOrigin(cwd, currentBranch) : Promise.resolve(null),
  ]);

  if (configured.isAppostleOwnedWorktree) {
    const mainRepoRoot = await getMainRepoRoot(cwd);
    return {
      isGit: true,
      repoRoot: worktreeRoot,
      mainRepoRoot,
      currentBranch,
      isDirty,
      baseRef: configured.baseRef,
      aheadBehind,
      aheadOfOrigin,
      behindOfOrigin,
      hasRemote,
      remoteUrl,
      isAppostleOwnedWorktree: true,
    };
  }

  return {
    isGit: true,
    repoRoot: worktreeRoot,
    currentBranch,
    isDirty,
    baseRef,
    aheadBehind,
    aheadOfOrigin,
    behindOfOrigin,
    hasRemote,
    remoteUrl,
    isAppostleOwnedWorktree: false,
  };
}

export interface CheckoutShortstat {
  additions: number;
  deletions: number;
}

/**
 * Parse `git diff --shortstat` output into additions/deletions.
 * Returns null when the diff is empty or can't be parsed.
 */
function parseShortstatOutput(stdout: string): CheckoutShortstat | null {
  const text = stdout.trim();
  if (!text) return null;

  let additions = 0;
  let deletions = 0;
  const addMatch = text.match(/(\d+)\s+insertion/);
  if (addMatch) additions = Number.parseInt(addMatch[1]!, 10);
  const delMatch = text.match(/(\d+)\s+deletion/);
  if (delMatch) deletions = Number.parseInt(delMatch[1]!, 10);

  if (additions === 0 && deletions === 0) return null;
  return { additions, deletions };
}

/**
 * Branch-level diff: all work since this branch diverged from its base
 * (commits + any uncommitted edits). Used by "comparing across branches"
 * surfaces like the workspace switcher and sidebar.
 *
 * Returns null for detached HEAD, repos without a resolvable base, or when
 * additions+deletions is zero. Always reads fresh — no cache; callers
 * invalidate via the workspace-git-service fs watchers.
 */
export async function getCheckoutBranchShortstat(
  cwd: string,
  context?: CheckoutContext,
): Promise<CheckoutShortstat | null> {
  try {
    await requireGitRepo(cwd);
  } catch {
    return null;
  }

  const configured = await getConfiguredBaseRefForCwd(cwd, context);
  const localBaseRef = configured.baseRef ?? (await resolveBaseRef(cwd));
  const currentBranch = await getCurrentBranch(cwd);

  let diffTarget: string;

  if (currentBranch && localBaseRef && currentBranch !== localBaseRef) {
    // Feature branch: diff against the merge-base with the base branch
    const comparisonBaseRef = await resolveBestComparisonBaseRef(cwd, localBaseRef);
    try {
      const { stdout } = await runGitCommand(["merge-base", "HEAD", comparisonBaseRef], {
        cwd,
        env: READ_ONLY_GIT_ENV,
      });
      const mergeBase = stdout.trim();
      if (!mergeBase) return null;
      diffTarget = mergeBase;
    } catch {
      return null;
    }
  } else if (currentBranch) {
    // On the base branch: diff against the remote tracking branch
    const hasOrigin = await doesGitRefExist(cwd, `refs/remotes/origin/${currentBranch}`);
    if (!hasOrigin) return null;
    diffTarget = `origin/${currentBranch}`;
  } else {
    return null;
  }

  try {
    // Omit HEAD so the diff includes uncommitted (staged + unstaged) changes too.
    const { stdout } = await runGitCommand(["diff", "--shortstat", diffTarget], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    return parseShortstatOutput(stdout);
  } catch {
    return null;
  }
}

/**
 * Working-tree diff: only what's uncommitted right now (HEAD -> worktree,
 * staged + unstaged + untracked). Used by "about to act in this workspace"
 * surfaces — the toggle-explorer button, the commit preview — so the
 * number always matches what the explorer panel shows in its default
 * uncommitted mode, which lists untracked files as "New" additions.
 *
 * `git diff --shortstat HEAD` alone misses untracked files (they're not
 * in the index), so we fold them in: list via `ls-files --others
 * --exclude-standard` and count lines directly, skipping binaries and
 * files over the per-file diff size limit (same rules as the diff pane).
 *
 * Returns null when nothing is uncommitted. Always reads fresh — no cache.
 */
export async function getCheckoutUncommittedShortstat(
  cwd: string,
): Promise<CheckoutShortstat | null> {
  try {
    await requireGitRepo(cwd);
  } catch {
    return null;
  }

  let additions = 0;
  let deletions = 0;

  // Tracked uncommitted (staged + unstaged vs HEAD).
  try {
    const { stdout } = await runGitCommand(["diff", "--shortstat", "HEAD"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const tracked = parseShortstatOutput(stdout);
    if (tracked) {
      additions += tracked.additions;
      deletions += tracked.deletions;
    }
  } catch {
    // Empty repo with no HEAD — still count untracked below.
  }

  // Untracked files — each line counts as an addition.
  try {
    const { stdout } = await runGitCommand(["ls-files", "--others", "--exclude-standard"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    const files = stdout
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    for (const file of files) {
      try {
        const inspected = await inspectUntrackedFile(cwd, file);
        if (!inspected.stat || inspected.stat.isBinary || inspected.truncated) continue;
        const absolutePath = resolve(cwd, file);
        const handle = await openFile(absolutePath, "r");
        try {
          const metadata = await handle.stat();
          const buffer = Buffer.alloc(Math.min(Number(metadata.size), PER_FILE_DIFF_MAX_BYTES));
          await handle.read(buffer, 0, buffer.length, 0);
          const text = buffer.toString("utf8");
          if (!text) continue;
          // Git counts each newline-terminated line as one addition; a file
          // without a trailing newline still has its final line counted.
          let count = 0;
          for (let i = 0; i < text.length; i += 1) {
            if (text.charCodeAt(i) === 10) count += 1;
          }
          if (text.length > 0 && text.charCodeAt(text.length - 1) !== 10) count += 1;
          additions += count;
        } finally {
          await handle.close();
        }
      } catch {
        // Skip unreadable files; treat them as zero additions.
      }
    }
  } catch {
    // Ignore — fall back to whatever we already accumulated from tracked diff.
  }

  if (additions === 0 && deletions === 0) return null;
  return { additions, deletions };
}

export interface UncommittedFileEntry {
  path: string;
  /** A=added, M=modified, D=deleted, R=renamed, C=copied. Mirrors `git diff --name-status`. */
  changeType: "A" | "M" | "D" | "R" | "C";
}

/**
 * Fast, lightweight listing of uncommitted files (tracked + untracked).
 * Just paths and coarse change type — no diff content, no line counts,
 * no binary detection. Intended for UI surfaces that need to show "what
 * changed" without paying the full diff cost (e.g. AI commit message
 * generation, which only needs filenames to describe intent).
 *
 * Returns an empty array for non-git dirs or fresh repos with no HEAD.
 */
export async function listUncommittedFiles(cwd: string): Promise<UncommittedFileEntry[]> {
  try {
    await requireGitRepo(cwd);
  } catch {
    return [];
  }

  const results: UncommittedFileEntry[] = [];

  // Tracked changes (staged + unstaged) vs HEAD.
  try {
    const { stdout } = await runGitCommand(["diff", "--name-status", "HEAD"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    for (const line of stdout
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)) {
      const parts = line.split("\t");
      const rawStatus = parts[0]?.trim();
      if (!rawStatus) continue;
      const code = rawStatus[0];
      if (!code) continue;
      if (code === "R" || code === "C") {
        const dest = parts[2];
        if (dest) results.push({ path: dest, changeType: code });
        continue;
      }
      const path = parts[1];
      if (!path) continue;
      if (code === "A" || code === "M" || code === "D") {
        results.push({ path, changeType: code });
      }
    }
  } catch {
    // No HEAD yet (empty repo) — fall through and just report untracked.
  }

  // Untracked files — appear as new additions from the commit's POV.
  try {
    const { stdout } = await runGitCommand(["ls-files", "--others", "--exclude-standard"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    for (const file of stdout
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)) {
      results.push({ path: file, changeType: "A" });
    }
  } catch {
    // Non-fatal.
  }

  return results;
}

export async function getCheckoutDiff(
  cwd: string,
  compare: CheckoutDiffCompare,
  context?: CheckoutContext,
): Promise<CheckoutDiffResult> {
  await requireGitRepo(cwd);

  let refForDiff: string;

  if (compare.mode === "uncommitted") {
    refForDiff = "HEAD";
  } else {
    const configured = await getConfiguredBaseRefForCwd(cwd, context);
    const baseRef = configured.baseRef ?? compare.baseRef ?? (await resolveBaseRef(cwd));
    if (!baseRef) {
      return { diff: "" };
    }
    if (configured.isAppostleOwnedWorktree && compare.baseRef && compare.baseRef !== baseRef) {
      throw new Error(`Base ref mismatch: expected ${baseRef}, got ${compare.baseRef}`);
    }

    const bestBaseRef = await resolveBestComparisonBaseRef(cwd, baseRef);
    refForDiff = (await tryResolveMergeBase(cwd, bestBaseRef)) ?? bestBaseRef;
  }

  const ignoreWhitespace = compare.ignoreWhitespace === true;
  const changes = await listCheckoutFileChanges(cwd, refForDiff, ignoreWhitespace);
  changes.sort((a, b) => {
    if (a.path === b.path) return 0;
    return a.path < b.path ? -1 : 1;
  });

  const structured: ParsedDiffFile[] = [];
  let diffText = "";
  let diffBytes = 0;
  const appendDiff = (text: string) => {
    if (!text) return;
    if (diffBytes >= TOTAL_DIFF_MAX_BYTES) return;
    const buf = Buffer.from(text, "utf8");
    if (diffBytes + buf.length <= TOTAL_DIFF_MAX_BYTES) {
      diffText += text;
      diffBytes += buf.length;
      return;
    }
    const remaining = TOTAL_DIFF_MAX_BYTES - diffBytes;
    if (remaining > 0) {
      diffText += buf.subarray(0, remaining).toString("utf8");
      diffBytes = TOTAL_DIFF_MAX_BYTES;
    }
  };

  const trackedChanges = changes.filter((change) => !change.isUntracked);
  const untrackedChanges = changes.filter((change) => change.isUntracked === true);
  const trackedChangeByPath = new Map(trackedChanges.map((change) => [change.path, change]));

  const trackedNumstatByPath =
    trackedChanges.length > 0
      ? await getTrackedNumstatByPath(cwd, refForDiff, ignoreWhitespace)
      : new Map<string, FileStat>();
  const trackedDiffPaths: string[] = [];
  const trackedPlaceholderByPath = new Map<
    string,
    { status: "binary" | "too_large"; stat: FileStat }
  >();

  for (const change of trackedChanges) {
    const stat = trackedNumstatByPath.get(change.path) ?? null;
    if (stat?.isBinary) {
      trackedPlaceholderByPath.set(change.path, { status: "binary", stat });
      continue;
    }
    if (isTrackedDiffTooLarge(stat)) {
      trackedPlaceholderByPath.set(change.path, { status: "too_large", stat });
      continue;
    }
    trackedDiffPaths.push(change.path);
  }

  let trackedDiffText = "";
  let trackedDiffTruncated = false;
  if (trackedDiffPaths.length > 0) {
    const trackedDiffResult = await runGitCommand(
      buildGitDiffArgs({
        ignoreWhitespace,
        extra: [refForDiff, "--", ...trackedDiffPaths],
      }),
      {
        cwd,
        env: READ_ONLY_GIT_ENV,
        maxOutputBytes: TOTAL_DIFF_MAX_BYTES,
      },
    );
    trackedDiffText = trackedDiffResult.stdout;
    trackedDiffTruncated = trackedDiffResult.truncated;
    appendDiff(trackedDiffText);
    if (trackedDiffTruncated) {
      appendDiff("# tracked diff truncated\n");
    }
  }

  const appendTrackedPlaceholderComment = (
    change: CheckoutFileChange,
    status: "binary" | "too_large",
  ) => {
    if (status === "binary") {
      appendDiff(`# ${change.path}: binary diff omitted\n`);
      return;
    }
    appendDiff(`# ${change.path}: diff too large omitted\n`);
  };

  if (compare.includeStructured) {
    const parsedTrackedFiles =
      trackedDiffText.length > 0
        ? await parseAndHighlightDiff(trackedDiffText, cwd, {
            getOldFileContent: async (file) => {
              const change = trackedChangeByPath.get(file.path);
              if (!change || change.isNew) {
                return null;
              }
              const refPath = change.oldPath ?? change.path;
              return readGitFileContentAtRef(cwd, refForDiff, refPath);
            },
          })
        : [];
    const parsedTrackedByPath = new Map(parsedTrackedFiles.map((file) => [file.path, file]));

    for (const change of trackedChanges) {
      const placeholder = trackedPlaceholderByPath.get(change.path);
      if (placeholder) {
        structured.push(
          buildPlaceholderParsedDiffFile(change, {
            status: placeholder.status,
            stat: placeholder.stat,
          }),
        );
        appendTrackedPlaceholderComment(change, placeholder.status);
        continue;
      }

      const stat = trackedNumstatByPath.get(change.path) ?? null;
      const parsedFile = parsedTrackedByPath.get(change.path);
      if (parsedFile) {
        structured.push({
          ...parsedFile,
          path: change.path,
          isNew: change.isNew,
          isDeleted: change.isDeleted,
          status: "ok",
        });
        continue;
      }

      // `git diff -w --name-status` can still report a modified path even when the
      // whitespace-filtered patch and numstat are both empty. Skip emitting a
      // structured placeholder in that case so whitespace-only edits truly disappear.
      if (
        ignoreWhitespace &&
        !trackedDiffTruncated &&
        change.status.startsWith("M") &&
        (!stat || (!stat.isBinary && stat.additions === 0 && stat.deletions === 0))
      ) {
        continue;
      }

      structured.push({
        path: change.path,
        isNew: change.isNew,
        isDeleted: change.isDeleted,
        additions: stat?.additions ?? 0,
        deletions: stat?.deletions ?? 0,
        hunks: [],
        status: trackedDiffTruncated ? "too_large" : "ok",
      });
    }
  } else {
    for (const change of trackedChanges) {
      const placeholder = trackedPlaceholderByPath.get(change.path);
      if (placeholder) {
        appendTrackedPlaceholderComment(change, placeholder.status);
      }
    }
  }

  for (const change of untrackedChanges) {
    if (diffBytes >= TOTAL_DIFF_MAX_BYTES) {
      break;
    }
    const { text, truncated, stat } = await getUntrackedDiffText(cwd, change, ignoreWhitespace);

    if (!compare.includeStructured) {
      if (stat?.isBinary) {
        appendDiff(`# ${change.path}: binary diff omitted\n`);
      } else if (truncated) {
        appendDiff(`# ${change.path}: diff too large omitted\n`);
      } else {
        appendDiff(text);
      }
      continue;
    }

    if (stat?.isBinary) {
      structured.push(buildPlaceholderParsedDiffFile(change, { status: "binary", stat }));
      appendDiff(`# ${change.path}: binary diff omitted\n`);
      continue;
    }

    if (truncated) {
      structured.push(buildPlaceholderParsedDiffFile(change, { status: "too_large", stat }));
      appendDiff(`# ${change.path}: diff too large omitted\n`);
      continue;
    }

    appendDiff(text);
    const parsed = await parseAndHighlightDiff(text, cwd);
    const parsedFile =
      parsed[0] ??
      ({
        path: change.path,
        isNew: change.isNew,
        isDeleted: change.isDeleted,
        additions: stat?.additions ?? 0,
        deletions: stat?.deletions ?? 0,
        hunks: [],
      } satisfies ParsedDiffFile);

    structured.push({
      ...parsedFile,
      path: change.path,
      isNew: change.isNew,
      isDeleted: change.isDeleted,
      status: "ok",
    });
  }

  if (compare.includeStructured) {
    return { diff: diffText, structured };
  }
  return { diff: diffText };
}

export async function commitChanges(
  cwd: string,
  options: { message: string; addAll?: boolean },
): Promise<void> {
  await requireGitRepo(cwd);
  if (options.addAll ?? true) {
    await runGitCommand(["add", "-A"], { cwd, timeout: 120_000 });
  }
  await runGitCommand(["-c", "commit.gpgsign=false", "commit", "-m", options.message], {
    cwd,
    timeout: 120_000,
  });
}

export async function commitAll(cwd: string, message: string): Promise<void> {
  await commitChanges(cwd, { message, addAll: true });
}

export async function mergeToBase(
  cwd: string,
  options: MergeToBaseOptions = {},
  context?: CheckoutContext,
): Promise<string> {
  await requireGitRepo(cwd);
  const currentBranch = await getCurrentBranch(cwd);
  const configured = await getConfiguredBaseRefForCwd(cwd, context);
  const baseRef = configured.baseRef ?? options.baseRef ?? (await resolveBaseRef(cwd));
  if (!baseRef) {
    throw new Error("Unable to determine base branch for merge");
  }
  if (configured.isAppostleOwnedWorktree && options.baseRef && options.baseRef !== baseRef) {
    throw new Error(`Base ref mismatch: expected ${baseRef}, got ${options.baseRef}`);
  }
  if (!currentBranch) {
    throw new Error("Unable to determine current branch for merge");
  }
  let normalizedBaseRef = baseRef;
  normalizedBaseRef = normalizeLocalBranchRefName(normalizedBaseRef);
  const currentWorktreeRoot = (await getWorktreeRoot(cwd)) ?? cwd;
  if (normalizedBaseRef === currentBranch) {
    return currentWorktreeRoot;
  }

  const baseWorktree = await getWorktreePathForBranch(cwd, normalizedBaseRef);
  const operationCwd = baseWorktree ?? currentWorktreeRoot;
  const isSameCheckout = resolve(operationCwd) === resolve(currentWorktreeRoot);
  const originalBranch = await getCurrentBranch(operationCwd);
  const mode = options.mode ?? "merge";
  try {
    await runGitCommand(["checkout", normalizedBaseRef], {
      cwd: operationCwd,
      timeout: 120_000,
    });
    if (mode === "squash") {
      await runGitCommand(["merge", "--squash", currentBranch], {
        cwd: operationCwd,
        timeout: 120_000,
      });
      const message =
        options.commitMessage ?? `Squash merge ${currentBranch} into ${normalizedBaseRef}`;
      await runGitCommand(["-c", "commit.gpgsign=false", "commit", "-m", message], {
        cwd: operationCwd,
        timeout: 120_000,
      });
    } else {
      await runGitCommand(["merge", currentBranch], { cwd: operationCwd, timeout: 120_000 });
    }
  } catch (error) {
    const errorDetails =
      error instanceof Error
        ? `${error.message}\n${(error as any).stderr ?? ""}\n${(error as any).stdout ?? ""}`
        : String(error);
    try {
      const [unmergedOutput, lsFilesOutput, statusOutput] = await Promise.all([
        runGitCommand(["diff", "--name-only", "--diff-filter=U"], { cwd: operationCwd }),
        runGitCommand(["ls-files", "-u"], { cwd: operationCwd }),
        runGitCommand(["status", "--porcelain"], { cwd: operationCwd }),
      ]);
      const statusConflicts = statusOutput.stdout
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .filter((line) => /^(UU|AA|DD|AU|UA|UD|DU)\s/.test(line))
        .map((line) => line.slice(3).trim());
      const conflicts = [
        ...unmergedOutput.stdout
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        ...lsFilesOutput.stdout
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => line.split("\t").pop() as string),
        ...statusConflicts,
      ].filter(Boolean);
      const conflictDetected =
        conflicts.length > 0 || /CONFLICT|Automatic merge failed/i.test(errorDetails);
      if (conflictDetected) {
        try {
          await runGitCommand(["merge", "--abort"], { cwd: operationCwd, timeout: 120_000 });
        } catch {
          // ignore
        }
        throw new MergeConflictError({
          baseRef: normalizedBaseRef,
          currentBranch,
          conflictFiles: conflicts.length > 0 ? conflicts : [],
        });
      }
    } catch (innerError) {
      if (innerError instanceof MergeConflictError) {
        throw innerError;
      }
      // ignore detection failures
    }

    throw error;
  } finally {
    if (isSameCheckout && originalBranch && originalBranch !== normalizedBaseRef) {
      try {
        await runGitCommand(["checkout", originalBranch], {
          cwd: operationCwd,
          timeout: 120_000,
        });
      } catch {
        // ignore
      }
    }
  }
  return operationCwd;
}

export async function mergeFromBase(
  cwd: string,
  options: MergeFromBaseOptions = {},
  context?: CheckoutContext,
): Promise<void> {
  await requireGitRepo(cwd);
  const currentBranch = await getCurrentBranch(cwd);
  if (!currentBranch || currentBranch === "HEAD") {
    throw new Error("Unable to determine current branch for merge");
  }

  const configured = await getConfiguredBaseRefForCwd(cwd, context);
  const baseRef = configured.baseRef ?? options.baseRef ?? (await resolveBaseRef(cwd));
  if (!baseRef) {
    throw new Error("Unable to determine base branch for merge");
  }
  if (configured.isAppostleOwnedWorktree && options.baseRef && options.baseRef !== baseRef) {
    throw new Error(`Base ref mismatch: expected ${baseRef}, got ${options.baseRef}`);
  }

  const requireCleanTarget = options.requireCleanTarget ?? true;
  if (requireCleanTarget) {
    const { stdout } = await runGitCommand(["status", "--porcelain"], {
      cwd,
      env: READ_ONLY_GIT_ENV,
    });
    if (stdout.trim().length > 0) {
      throw new Error("Working directory has uncommitted changes.");
    }
  }

  const normalizedBaseRef = normalizeLocalBranchRefName(baseRef);
  const bestBaseRef = await resolveMostAheadBaseRef(cwd, normalizedBaseRef);
  if (bestBaseRef === currentBranch) {
    return;
  }

  try {
    await runGitCommand(["merge", bestBaseRef], { cwd, timeout: 120_000 });
  } catch (error) {
    const errorDetails =
      error instanceof Error
        ? `${error.message}\n${(error as any).stderr ?? ""}\n${(error as any).stdout ?? ""}`
        : String(error);
    try {
      const [unmergedOutput, lsFilesOutput, statusOutput] = await Promise.all([
        runGitCommand(["diff", "--name-only", "--diff-filter=U"], { cwd }),
        runGitCommand(["ls-files", "-u"], { cwd }),
        runGitCommand(["status", "--porcelain"], { cwd }),
      ]);
      const statusConflicts = statusOutput.stdout
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .filter((line) => /^(UU|AA|DD|AU|UA|UD|DU)\s/.test(line))
        .map((line) => line.slice(3).trim());
      const conflicts = [
        ...unmergedOutput.stdout
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        ...lsFilesOutput.stdout
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => line.split("\t").pop() as string),
        ...statusConflicts,
      ].filter(Boolean);
      const conflictDetected =
        conflicts.length > 0 || /CONFLICT|Automatic merge failed/i.test(errorDetails);
      if (conflictDetected) {
        try {
          await runGitCommand(["merge", "--abort"], { cwd, timeout: 120_000 });
        } catch {
          // ignore
        }
        throw new MergeFromBaseConflictError({
          baseRef: bestBaseRef,
          currentBranch,
          conflictFiles: conflicts.length > 0 ? conflicts : [],
        });
      }
    } catch (innerError) {
      if (innerError instanceof MergeFromBaseConflictError) {
        throw innerError;
      }
      // ignore detection failures
    }

    throw error;
  }
}

export async function pullCurrentBranch(cwd: string, github?: GitHubService): Promise<void> {
  await requireGitRepo(cwd);
  const currentBranch = await getCurrentBranch(cwd);
  if (!currentBranch || currentBranch === "HEAD") {
    throw new Error("Unable to determine current branch for pull");
  }
  const hasRemote = await hasOriginRemote(cwd);
  if (!hasRemote) {
    throw new Error("Remote 'origin' is not configured.");
  }
  try {
    await runGitCommand(["pull"], { cwd, timeout: 120_000 });
    github?.invalidate({ cwd });
  } catch (error) {
    await abortGitPullConflictState(cwd);
    throw error;
  }
}

export async function pushCurrentBranch(cwd: string, github?: GitHubService): Promise<void> {
  await requireGitRepo(cwd);
  const currentBranch = await getCurrentBranch(cwd);
  if (!currentBranch || currentBranch === "HEAD") {
    throw new Error("Unable to determine current branch for push");
  }
  const hasRemote = await hasOriginRemote(cwd);
  if (!hasRemote) {
    throw new Error("Remote 'origin' is not configured.");
  }
  await runGitCommand(["push", "-u", "origin", currentBranch], { cwd, timeout: 120_000 });
  github?.invalidate({ cwd });
}

export interface CreatePullRequestOptions {
  title: string;
  body?: string;
  base?: string;
  head?: string;
  draft?: boolean;
}

export interface PullRequestStatus {
  number?: number;
  repoOwner?: string;
  repoName?: string;
  url: string;
  title: string;
  state: string;
  baseRefName: string;
  headRefName: string;
  isMerged: boolean;
  isDraft?: boolean;
  checks?: PullRequestCheck[];
  checksStatus?: ChecksStatus;
  reviewDecision?: ReviewDecision;
}

export interface PullRequestStatusResult {
  status: PullRequestStatus | null;
  githubFeaturesEnabled: boolean;
}

export type PullRequestCheck = {
  name: string;
  status: "success" | "failure" | "pending" | "skipped" | "cancelled";
  url: string | null;
  workflow?: string;
  duration?: string;
};

export type ChecksStatus = "none" | "pending" | "success" | "failure";

export type ReviewDecision = "approved" | "changes_requested" | "pending" | null;

export async function createPullRequest(
  cwd: string,
  options: CreatePullRequestOptions,
  github: GitHubService = createGitHubService(),
): Promise<{ url: string; number: number }> {
  await requireGitRepo(cwd);
  const repo = await resolveGitHubRepo(cwd);
  if (!repo) {
    throw new Error("Unable to determine GitHub repo from git remote");
  }

  const head = options.head ?? (await getCurrentBranch(cwd));
  const configured = await getConfiguredBaseRefForCwd(cwd);
  const base = configured.baseRef ?? options.base ?? (await resolveBaseRef(cwd));
  if (!head) {
    throw new Error("Unable to determine head branch for PR");
  }
  if (!base) {
    throw new Error("Unable to determine base branch for PR");
  }
  const normalizedBase = normalizeLocalBranchRefName(base);
  if (configured.isAppostleOwnedWorktree && options.base && options.base !== base) {
    throw new Error(`Base ref mismatch: expected ${base}, got ${options.base}`);
  }

  await runGitCommand(["push", "-u", "origin", head], { cwd, timeout: 120_000 });

  const result = await github.createPullRequest({
    cwd,
    repo,
    title: options.title,
    body: options.body,
    head,
    base: normalizedBase,
  });
  github.invalidate({ cwd });
  return result;
}

export async function getPullRequestStatus(
  cwd: string,
  github: GitHubService = createGitHubService(),
): Promise<PullRequestStatusResult> {
  const cacheKey = getPullRequestStatusCacheKey(cwd);
  const cached = pullRequestStatusCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const existing = pullRequestStatusInFlight.get(cacheKey);
  if (existing) {
    return existing;
  }

  const lookup = getPullRequestStatusUncached(cwd, github)
    .then((status) => {
      pullRequestStatusCache.set(cacheKey, status);
      return status;
    })
    .finally(() => {
      pullRequestStatusInFlight.delete(cacheKey);
    });

  pullRequestStatusInFlight.set(cacheKey, lookup);
  return lookup;
}

async function getPullRequestStatusUncached(
  cwd: string,
  github: GitHubService,
): Promise<PullRequestStatusResult> {
  await requireGitRepo(cwd);
  const head = await getCurrentBranch(cwd);
  if (!head) {
    return {
      status: null,
      githubFeaturesEnabled: false,
    };
  }
  try {
    const status = await github.getCurrentPullRequestStatus({ cwd, headRef: head });
    return {
      status,
      githubFeaturesEnabled: true,
    };
  } catch (error) {
    if (error instanceof GitHubCliMissingError || error instanceof GitHubAuthenticationError) {
      return { status: null, githubFeaturesEnabled: false };
    }
    throw error;
  }
}

export interface GitHubSearchResult {
  items: Array<{
    kind: "issue" | "pr";
    number: number;
    title: string;
    url: string;
    state: string;
    body: string | null;
    labels: string[];
    baseRefName?: string | null;
    headRefName?: string | null;
    updatedAt?: string;
  }>;
  githubFeaturesEnabled: boolean;
}

interface SearchGitHubIssuesAndPrsOptions {
  kinds?: GitHubSearchKind[];
}

export async function searchGitHubIssuesAndPrs(
  cwd: string,
  query: string,
  limit = 20,
  github: GitHubService = createGitHubService(),
  options: SearchGitHubIssuesAndPrsOptions = {},
): Promise<GitHubSearchResult> {
  await requireGitRepo(cwd);
  try {
    const kinds = options.kinds ?? ["github-issue", "github-pr"];
    const shouldFetchIssues = kinds.includes("github-issue");
    const shouldFetchPullRequests = kinds.includes("github-pr");
    const issuesResult = shouldFetchIssues
      ? await github.listIssues({ cwd, query, limit }).then(
          (value) => ({ status: "fulfilled" as const, value }),
          (reason) => ({ status: "rejected" as const, reason }),
        )
      : null;
    const prsResult = shouldFetchPullRequests
      ? await github.listPullRequests({ cwd, query, limit }).then(
          (value) => ({ status: "fulfilled" as const, value }),
          (reason) => ({ status: "rejected" as const, reason }),
        )
      : null;

    const items: GitHubSearchResult["items"] = [];
    const requestedResults = [issuesResult, prsResult].filter((result) => result !== null);
    const failures = requestedResults.filter((result) => result.status === "rejected");
    if (
      requestedResults.length > 0 &&
      failures.length === requestedResults.length &&
      failures.every(
        (result) =>
          result.status === "rejected" &&
          (result.reason instanceof GitHubCliMissingError ||
            result.reason instanceof GitHubAuthenticationError),
      )
    ) {
      return { items: [], githubFeaturesEnabled: false };
    }

    if (issuesResult?.status === "fulfilled") {
      for (const item of issuesResult.value) {
        items.push({
          kind: "issue",
          number: item.number,
          title: item.title,
          url: item.url,
          state: item.state,
          body: item.body,
          labels: item.labels,
          baseRefName: null,
          headRefName: null,
          updatedAt: item.updatedAt,
        });
      }
    }

    if (prsResult?.status === "fulfilled") {
      for (const item of prsResult.value) {
        items.push({
          kind: "pr",
          number: item.number,
          title: item.title,
          url: item.url,
          state: item.state,
          body: item.body,
          labels: item.labels,
          baseRefName: item.baseRefName,
          headRefName: item.headRefName,
          updatedAt: item.updatedAt,
        });
      }
    }

    return { items, githubFeaturesEnabled: true };
  } catch (error) {
    if (error instanceof GitHubCliMissingError || error instanceof GitHubAuthenticationError) {
      return { items: [], githubFeaturesEnabled: false };
    }
    throw error;
  }
}

export interface CreateGitHubIssueRequestPayload {
  title: string;
  body?: string;
  labels?: string[];
}

export interface CreateGitHubIssueResult {
  item: {
    kind: "issue";
    number: number;
    title: string;
    url: string;
    state: string;
    body: string | null;
    labels: string[];
    updatedAt?: string;
  } | null;
  githubFeaturesEnabled: boolean;
}

export async function createGitHubIssue(
  cwd: string,
  payload: CreateGitHubIssueRequestPayload,
  github: GitHubService = createGitHubService(),
): Promise<CreateGitHubIssueResult> {
  await requireGitRepo(cwd);
  // Issue creation goes through `gh issue create -t <title> [-b <body>]
  // [-l <label>]`. We re-read the issue afterward to surface the canonical
  // shape that the search RPC returns, so the client can drop the new issue
  // straight into its result list.
  try {
    const args = ["issue", "create", "--title", payload.title];
    const trimmedBody = payload.body?.trim();
    args.push("--body", trimmedBody && trimmedBody.length > 0 ? trimmedBody : "");
    for (const label of payload.labels ?? []) {
      if (label.trim().length === 0) continue;
      args.push("--label", label);
    }
    const stdout = await ghCreateIssue(github, cwd, args);
    const url = stdout.split(/\s+/).find((token) => /^https?:\/\//.test(token));
    const numberFromUrl = url ? extractGitHubIssueNumber(url) : null;
    if (!url || numberFromUrl === null) {
      // Fallback — re-list and grab the most recent issue. Edge case.
      const recent = await github.listIssues({ cwd, query: "", limit: 1 });
      const top = recent[0];
      if (!top) {
        return { item: null, githubFeaturesEnabled: true };
      }
      return {
        item: {
          kind: "issue",
          number: top.number,
          title: top.title,
          url: top.url,
          state: top.state,
          body: top.body,
          labels: top.labels,
          updatedAt: top.updatedAt,
        },
        githubFeaturesEnabled: true,
      };
    }
    github.invalidate({ cwd });
    return {
      item: {
        kind: "issue",
        number: numberFromUrl,
        title: payload.title,
        url,
        state: "open",
        body: trimmedBody && trimmedBody.length > 0 ? trimmedBody : null,
        labels: [...(payload.labels ?? [])],
      },
      githubFeaturesEnabled: true,
    };
  } catch (error) {
    if (error instanceof GitHubCliMissingError || error instanceof GitHubAuthenticationError) {
      return { item: null, githubFeaturesEnabled: false };
    }
    throw error;
  }
}

function extractGitHubIssueNumber(url: string): number | null {
  const match = url.match(/\/issues\/(\d+)(?:[?#].*)?$/);
  if (!match) return null;
  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) ? parsed : null;
}

// Thin shell-out helper that bypasses the cached service for the create
// path. We don't want creates to hit the read-cache; the service's
// `invalidate` call after success refreshes downstream lists.
async function ghCreateIssue(_github: GitHubService, cwd: string, args: string[]): Promise<string> {
  const ghPath = await findExecutable("gh");
  if (!ghPath) {
    throw new GitHubCliMissingError();
  }
  try {
    const { stdout } = await execCommand("gh", args, {
      cwd,
      env: { ...process.env, GIT_TERMINAL_PROMPT: "0" },
      maxBuffer: 10 * 1024 * 1024,
    });
    return stdout.trim();
  } catch (error) {
    const stderr =
      error && typeof error === "object" && "stderr" in error
        ? String((error as { stderr?: unknown }).stderr ?? "")
        : "";
    if (
      stderr.toLowerCase().includes("gh auth login") ||
      stderr.toLowerCase().includes("authentication")
    ) {
      throw new GitHubAuthenticationError({ stderr });
    }
    throw error;
  }
}

// ============================================================================
// GitLab — parallel to the GitHub helpers above, wrapping the `glab` CLI.
// ============================================================================

export interface GitLabSearchResult {
  items: Array<{
    kind: "issue" | "mr";
    iid: number;
    title: string;
    url: string;
    state: string;
    body: string | null;
    labels: string[];
    sourceBranch?: string | null;
    targetBranch?: string | null;
    updatedAt?: string;
  }>;
  gitlabFeaturesEnabled: boolean;
}

interface SearchGitLabOptions {
  kinds?: GitLabSearchKind[];
}

export async function searchGitLabIssuesAndMrs(
  cwd: string,
  query: string,
  limit = 20,
  gitlab: GitLabService = createGitLabService(),
  options: SearchGitLabOptions = {},
): Promise<GitLabSearchResult> {
  await requireGitRepo(cwd);
  try {
    const kinds = options.kinds ?? ["gitlab-issue", "gitlab-mr"];
    const shouldFetchIssues = kinds.includes("gitlab-issue");
    const shouldFetchMrs = kinds.includes("gitlab-mr");
    const issuesResult = shouldFetchIssues
      ? await gitlab.listIssues({ cwd, query, limit }).then(
          (value) => ({ status: "fulfilled" as const, value }),
          (reason) => ({ status: "rejected" as const, reason }),
        )
      : null;
    const mrsResult = shouldFetchMrs
      ? await gitlab.listMergeRequests({ cwd, query, limit }).then(
          (value) => ({ status: "fulfilled" as const, value }),
          (reason) => ({ status: "rejected" as const, reason }),
        )
      : null;

    const items: GitLabSearchResult["items"] = [];
    const requestedResults = [issuesResult, mrsResult].filter((result) => result !== null);
    const failures = requestedResults.filter((result) => result.status === "rejected");
    if (
      requestedResults.length > 0 &&
      failures.length === requestedResults.length &&
      failures.every(
        (result) =>
          result.status === "rejected" &&
          (result.reason instanceof GitLabCliMissingError ||
            result.reason instanceof GitLabAuthenticationError),
      )
    ) {
      return { items: [], gitlabFeaturesEnabled: false };
    }

    if (issuesResult?.status === "fulfilled") {
      for (const item of issuesResult.value) {
        items.push({
          kind: "issue",
          iid: item.iid,
          title: item.title,
          url: item.url,
          state: item.state,
          body: item.body,
          labels: item.labels,
          sourceBranch: null,
          targetBranch: null,
          updatedAt: item.updatedAt,
        });
      }
    }

    if (mrsResult?.status === "fulfilled") {
      for (const item of mrsResult.value) {
        items.push({
          kind: "mr",
          iid: item.iid,
          title: item.title,
          url: item.url,
          state: item.state,
          body: item.body,
          labels: item.labels,
          sourceBranch: item.sourceBranch,
          targetBranch: item.targetBranch,
          updatedAt: item.updatedAt,
        });
      }
    }

    return { items, gitlabFeaturesEnabled: true };
  } catch (error) {
    if (error instanceof GitLabCliMissingError || error instanceof GitLabAuthenticationError) {
      return { items: [], gitlabFeaturesEnabled: false };
    }
    throw error;
  }
}

export interface CreateGitLabIssueRequestPayload {
  title: string;
  body?: string;
  labels?: string[];
}

export interface CreateGitLabIssueResult {
  item: {
    kind: "issue";
    iid: number;
    title: string;
    url: string;
    state: string;
    body: string | null;
    labels: string[];
    updatedAt?: string;
  } | null;
  gitlabFeaturesEnabled: boolean;
}

export async function createGitLabIssue(
  cwd: string,
  payload: CreateGitLabIssueRequestPayload,
  gitlab: GitLabService = createGitLabService(),
): Promise<CreateGitLabIssueResult> {
  await requireGitRepo(cwd);
  try {
    const created = await gitlab.createIssue({
      cwd,
      title: payload.title,
      body: payload.body,
      labels: payload.labels,
    });
    return {
      item: {
        kind: "issue",
        iid: created.iid,
        title: created.title,
        url: created.url,
        state: created.state,
        body: created.body,
        labels: created.labels,
        updatedAt: created.updatedAt,
      },
      gitlabFeaturesEnabled: true,
    };
  } catch (error) {
    if (error instanceof GitLabCliMissingError || error instanceof GitLabAuthenticationError) {
      return { item: null, gitlabFeaturesEnabled: false };
    }
    throw error;
  }
}
